// Tracks real page-loading activity — every fetch() call and every image/
// video/iframe that gets mounted — so the loading screen can wait for the
// page to actually be ready instead of guessing with a timer. Installed as
// a module-level side effect, so it must be imported before anything else
// starts fetching (see main.tsx: imported first, before App).

type Listener = () => void;

let activeFetches = 0;
let activeMedia = 0;
let lastActivityAt = typeof performance !== 'undefined' ? performance.now() : 0;
const listeners = new Set<Listener>();

function notify() {
  lastActivityAt = performance.now();
  listeners.forEach((l) => l());
}

if (typeof window !== 'undefined' && !(window as unknown as { __hsFetchPatched?: boolean }).__hsFetchPatched) {
  (window as unknown as { __hsFetchPatched: boolean }).__hsFetchPatched = true;
  const originalFetch = window.fetch.bind(window);
  window.fetch = (...args: Parameters<typeof fetch>) => {
    activeFetches++;
    notify();
    return originalFetch(...args).finally(() => {
      activeFetches--;
      notify();
    });
  };
}

type Loadable = HTMLImageElement | HTMLVideoElement | HTMLIFrameElement;

function isLoadable(node: Node): node is Loadable {
  return node instanceof HTMLImageElement || node instanceof HTMLVideoElement || node instanceof HTMLIFrameElement;
}

/** Only the critical above-the-fold images should hold the loading screen
 * open. `loading="lazy"` images are intentionally deferred until they near
 * the viewport — on a throttled connection they may never load during the
 * initial visit, which used to pin activeMedia above 0 forever and stall
 * the loading screen at its MAX_WAIT_MS safety cap. Videos/iframes (guest
 * interview embeds etc.) are always secondary, below-the-fold content and
 * their load semantics (preload="metadata", third-party embed scripts) are
 * too unreliable to gate a first-paint experience on. */
function shouldTrack(el: Loadable): boolean {
  if (el instanceof HTMLImageElement) return el.loading !== 'lazy';
  return false;
}

function isAlreadyLoaded(el: Loadable): boolean {
  if (el instanceof HTMLImageElement) return el.complete;
  return false;
}

function trackMedia(el: Loadable) {
  if (!shouldTrack(el)) return;
  if (isAlreadyLoaded(el)) return;
  activeMedia++;
  notify();
  const done = () => {
    activeMedia--;
    notify();
    el.removeEventListener('load', done);
    el.removeEventListener('error', done);
    el.removeEventListener('loadeddata', done);
  };
  el.addEventListener('load', done);
  el.addEventListener('error', done);
  el.addEventListener('loadeddata', done);
}

function scanForMedia(root: Node) {
  if (isLoadable(root)) trackMedia(root);
  if (root instanceof Element) {
    root.querySelectorAll('img, video, iframe').forEach((el) => trackMedia(el as Loadable));
  }
}

if (typeof document !== 'undefined') {
  scanForMedia(document.documentElement);
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => scanForMedia(node));
    }
  });
  observer.observe(document.documentElement, { childList: true, subtree: true });
}

export function subscribeLoadActivity(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getLoadStats() {
  return { activeFetches, activeMedia, lastActivityAt };
}
